import { useEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Validates and handles the optional language parameter in the URL
 * Supports the pattern: {basePath}/{language}/{remainingParts}
 * where language can be 'en', 'ar', or omitted (defaults to 'en')
 */
export const LanguageValidator = () => {
  const { lang } = useParams<{ lang?: string }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    // Check if lang parameter is present and valid
    if (lang) {
      if (lang === 'en' || lang === 'ar') {
        // Valid language code - sync with i18n if needed
        if (i18n.language !== lang) {
          i18n.changeLanguage(lang);
        }
      } else {
        // Invalid language code - this means the 'lang' is actually part of the route
        // For example: /partners (where 'partners' was captured as lang)
        // In this case, do nothing - the route will be handled normally
        // and we'll use the default language or the one from localStorage

        // Make sure i18n language is set to something valid
        if (i18n.language !== 'en' && i18n.language !== 'ar') {
          const savedLang = localStorage.getItem('i18nextLng');
          const defaultLang = (savedLang === 'ar' || savedLang === 'en') ? savedLang : 'en';
          i18n.changeLanguage(defaultLang);
        }
      }
    } else {
      // No lang parameter - backward compatibility mode
      // Use the language from localStorage or default to 'en'
      const savedLang = localStorage.getItem('i18nextLng');
      const defaultLang = (savedLang === 'ar' || savedLang === 'en') ? savedLang : 'en';

      if (i18n.language !== defaultLang) {
        i18n.changeLanguage(defaultLang);
      }
    }
  }, [lang, i18n]);

  return <Outlet />;
};

export default LanguageValidator;
